// Machine credentials settings — `api_keys` (docs/09-frontend-surface.md §4),
// List archetype (UI-LAW §4c): the table is the primary content, create lives
// in a right-anchored Drawer (`?new=1`), and the one-time plaintext reveal is
// a Modal case (see ApiKeysSettingsView.tsx for the presentational half).
// Route segment mirrors the kebab resource path `/v1/api-keys`. The plaintext
// key is returned exactly once on create (never stored or re-surfaced); no
// token is kept in browser storage. URL state: `?new=1` (create drawer) and
// `?agent_id=<uuid>` (list filter — the API's own query param, docs/09 §7 noun
// parity; unrelated to the root `?agent` scope, which this screen ignores).
// Every search write is a functional merge so `agent_id`, `new`, and the root
// `agent` param survive each other's changes.

import { useState } from "react";

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { apiClient } from "../lib/apiClient";
import { useCallerAuthority } from "../lib/authority";
import { ApiRequestError, requestErrorDescription } from "../lib/errors";
import { queryKeys } from "../lib/queryKeys";
import { useListQuery } from "../lib/useListQuery";
import { useResolvedWorkingTeamId } from "../lib/workingScope";
import { parseApiKeysSearch, type ApiKeysSearch } from "../lib/routerSearch";
import {
  mergeSearch,
  useTypedNavigate,
  useTypedSearch,
} from "../lib/typedRouter";
import { useToast } from "../ui";
import type {
  ApiKeyCatalogTarget,
  ApiKeyCreateInput,
} from "./ApiKeyCreateDrawer";
import type { CreatedApiKey } from "./ApiKeySecretModal";
import { ApiKeysSettingsView } from "./ApiKeysSettingsView";

const agentSelectorListFilter = { surface: "api-key-scope-selector" } as const;

export function ApiKeysSettings() {
  const queryClient = useQueryClient();
  const toast = useToast();
  const navigate = useTypedNavigate<ApiKeysSearch>();
  const search = useTypedSearch(parseApiKeysSearch);
  const resolvedWorkingTeamId = useResolvedWorkingTeamId();
  const authority = useCallerAuthority();
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [catalogTarget, setCatalogTarget] = useState<
    ApiKeyCatalogTarget | null | undefined
  >(null);

  const agentIdFilter =
    typeof search.agent_id === "string" && search.agent_id.length > 0
      ? search.agent_id
      : undefined;
  const isCreateDrawerOpen = search.new === 1;

  // Same-route search writes are ALWAYS functional merges
  // (api-keys-settings.md), typed through the shared router helper.
  function patchSearch(patch: ApiKeysSearch, replace: boolean): void {
    void navigate({
      replace,
      search: mergeSearch<ApiKeysSearch>(patch),
    });
  }

  const agents = useListQuery("/v1/agents", {
    queryKey: queryKeys.agents.list(agentSelectorListFilter),
  });
  const teams = useListQuery("/v1/teams", {
    queryKey: queryKeys.teams.list({ surface: "api-key-scope-selector" }),
  });
  const effectiveCatalogTarget =
    !authority.isPending && !authority.isOrgOwner && catalogTarget === null
      ? resolvedWorkingTeamId == null
        ? undefined
        : { teamId: resolvedWorkingTeamId }
      : catalogTarget;

  // Filter rides inside the query key: a filter change is a fresh keyset
  // stream, never an offset synthesis.
  const apiKeyListFilter =
    agentIdFilter != null ? { agent_id: agentIdFilter } : {};
  const apiKeys = useListQuery("/v1/api-keys", {
    params: apiKeyListFilter,
    queryKey: queryKeys.apiKeys.list(apiKeyListFilter),
  });

  const scopeCatalog = useQuery({
    enabled: isCreateDrawerOpen && effectiveCatalogTarget !== undefined,
    queryKey: queryKeys.apiKeys.scopeCatalog({
      agent_id: effectiveCatalogTarget?.agentId,
      team_id: effectiveCatalogTarget?.teamId,
    }),
    queryFn: async () => {
      const { data, error } = await apiClient.GET(
        "/v1/api-keys:scope-catalog",
        {
          params: {
            query:
              effectiveCatalogTarget == null
                ? {}
                : {
                    agent_id: effectiveCatalogTarget.agentId,
                    team_id: effectiveCatalogTarget.teamId,
                  },
          },
        },
      );
      if (error) {
        throw new ApiRequestError(error);
      }
      return data;
    },
  });

  const createApiKey = useMutation({
    // The plaintext key must never enter react-query state: the secret is
    // seeded into component state HERE and the mutationFn returns void, so
    // `createApiKey.data` (mutation cache, devtools) can never hold it —
    // the component state below is the plaintext's only home, feeding the
    // one-time Modal.
    mutationFn: async (input: ApiKeyCreateInput) => {
      const { data, error } = await apiClient.POST("/v1/api-keys", {
        body: {
          agent_id: input.agentId,
          team_id: input.teamId,
          display_name: input.displayName,
          scopes: input.scopes,
        },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
      setCreatedKey({
        agent_id: data.agent_id,
        team_id: data.team_id,
        display_name: data.display_name,
        key_prefix: data.key_prefix,
        plaintext_key: data.plaintext_key,
      });
    },
    onSuccess: async () => {
      // Success toast never carries the plaintext key.
      toast.success("API key created");
      patchSearch({ new: undefined }, true);
      await queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
    },
    onError: (error) => {
      toast.error("API key create failed", {
        description: requestErrorDescription(error, "API key request failed."),
      });
    },
  });
  const revokeApiKey = useMutation({
    mutationFn: async (id: string) => {
      const { error } = await apiClient.DELETE("/v1/api-keys/{id}", {
        params: { path: { id } },
      });
      if (error) {
        throw new ApiRequestError(error);
      }
    },
    onSuccess: async () => {
      toast.success("API key revoked");
      await queryClient.invalidateQueries({ queryKey: queryKeys.apiKeys.all });
    },
    onError: (error) => {
      toast.error("API key revoke failed", {
        description: requestErrorDescription(error, "API key request failed."),
      });
    },
  });

  return (
    <ApiKeysSettingsView
      agentIdFilter={agentIdFilter}
      agents={agents.items}
      agentsErrorMessage={agents.errorMessage}
      apiKeys={apiKeys.items}
      apiKeysErrorMessage={apiKeys.errorMessage}
      canCreateOrgWide={authority.isOrgOwner}
      createdKey={createdKey}
      createErrorMessage={
        createApiKey.isError ? createApiKey.error.message : undefined
      }
      hasMoreAgents={agents.hasNextPage}
      hasMoreApiKeys={apiKeys.hasNextPage}
      hasMoreTeams={teams.hasNextPage}
      isAgentsFetchingNextPage={agents.isFetchingNextPage}
      isAgentsPending={agents.isPending}
      isApiKeysFetchingNextPage={apiKeys.isFetchingNextPage}
      isApiKeysPending={apiKeys.isPending}
      isTeamsFetchingNextPage={teams.isFetchingNextPage}
      isCreateDrawerOpen={isCreateDrawerOpen && !authority.isPending}
      isCreatePending={createApiKey.isPending}
      isScopeCatalogPending={
        effectiveCatalogTarget !== undefined && scopeCatalog.isPending
      }
      initialTeamId={effectiveCatalogTarget?.teamId}
      listStateKey={apiKeys.listStateKey}
      onAgentIdFilterChange={(value) => {
        patchSearch({ agent_id: value.length > 0 ? value : undefined }, true);
      }}
      onCloseCreateDrawer={() => {
        // Reset any stale create error so `?new=1` reopens a clean drawer.
        createApiKey.reset();
        setCatalogTarget(null);
        patchSearch({ new: undefined }, true);
      }}
      onCatalogTargetChange={setCatalogTarget}
      onCreateSubmit={(input) => {
        setCreatedKey(null);
        createApiKey.mutate(input);
      }}
      onDismissCreatedKey={() => {
        setCreatedKey(null);
      }}
      onFetchNextAgents={agents.fetchNextPage}
      onFetchNextApiKeys={apiKeys.fetchNextPage}
      onFetchNextTeams={teams.fetchNextPage}
      onOpenCreateDrawer={() => {
        const workingTeamId = resolvedWorkingTeamId;
        setCatalogTarget(
          workingTeamId == null ? null : { teamId: workingTeamId },
        );
        patchSearch({ new: 1 }, false);
      }}
      onResetRevoke={() => {
        // Reset any stale revoke error so the next confirm opens clean.
        revokeApiKey.reset();
      }}
      onRevoke={async (id) => {
        await revokeApiKey.mutateAsync(id);
      }}
      revokeErrorMessage={
        revokeApiKey.isError ? revokeApiKey.error.message : undefined
      }
      revokingApiKeyId={
        revokeApiKey.isPending ? revokeApiKey.variables : undefined
      }
      scopeCatalog={
        effectiveCatalogTarget === undefined ? undefined : scopeCatalog.data
      }
      scopeCatalogErrorMessage={
        effectiveCatalogTarget !== undefined && scopeCatalog.isError
          ? requestErrorDescription(
              scopeCatalog.error,
              "API key request failed.",
            )
          : undefined
      }
      teams={teams.items}
      teamsErrorMessage={teams.errorMessage}
    />
  );
}
